export type AdminErrorPayload = string | { code?: string; message?: string; details?: unknown };

export class ApiContractError extends Error {
  readonly code: string;
  readonly status?: number;
  readonly requestId?: string;
  readonly details?: unknown;

  constructor(message: string, options: { code?: string; status?: number; requestId?: string; details?: unknown } = {}) {
    super(message);
    this.name = 'ApiContractError';
    this.code = options.code ?? 'ADMIN_API_ERROR';
    this.status = options.status;
    this.requestId = options.requestId;
    this.details = options.details;
  }
}

export type AdminEnvelope<T> = {
  success?: boolean;
  data?: T;
  error?: AdminErrorPayload;
  requestId?: string;
};

export function normalizeAdminResponse<T>(payload: T | AdminEnvelope<T>): { data: T; requestId?: string } {
  if (!payload || typeof payload !== 'object' || !('success' in payload) && !('data' in payload) && !('error' in payload)) {
    return { data: payload as T };
  }
  const envelope = payload as AdminEnvelope<T>;
  if (envelope.success === false || envelope.error) {
    const error = envelope.error;
    const message = typeof error === 'string' ? error : error?.message ?? '管理接口请求失败';
    const code = typeof error === 'object' && error?.code ? error.code : 'ADMIN_API_ERROR';
    throw new ApiContractError(message, { code, requestId: envelope.requestId, details: typeof error === 'object' ? error?.details : undefined });
  }
  return { data: ('data' in envelope ? envelope.data : payload) as T, requestId: envelope.requestId };
}

export type AdminPage<T> = { items: T[]; total: number; page: number; pageSize: number };

export function normalizePage<T>(payload: unknown): AdminPage<T> {
  const value = (payload && typeof payload === 'object' ? payload : {}) as Record<string, unknown>;
  const items = Array.isArray(value.items) ? value.items : Array.isArray(value.data) ? value.data : [];
  const page = Number(value.page ?? 1) || 1;
  const pageSize = Number(value.pageSize ?? value.limit ?? (items.length || 1)) || 1;
  return { items: items as T[], total: Number(value.total ?? items.length) || 0, page, pageSize };
}

export function toChinaDateTime(value: string | number | Date): string {
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return '—';
  return new Intl.DateTimeFormat('zh-CN', {
    timeZone: 'Asia/Shanghai', year: 'numeric', month: '2-digit', day: '2-digit',
    hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false,
  }).format(date).replace(/\//g, '-');
}
